<template>
  <a-table
    :columns="columns"
    :data-source="items"
    :loading="loading"
    :pagination="false"
    :locale="{ emptyText: tv('tableEmpty') }"
    :expandable="expandable"
    row-key="id"
    :scroll="{ x: 880 }"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'fileName'">
        <span>{{ record.file_name || tv('unnamedFile') }}</span>
      </template>

      <template v-else-if="column.key === 'language'">
        <span>{{ languagePairLabel(record) }}</span>
      </template>

      <template v-else-if="column.key === 'status'">
        <a-space>
          <a-tag :color="workItemStatusColor(displayStage(record))">
            {{ statusLabel(displayStage(record)) }}
          </a-tag>
          <a-tag v-if="showRoundBadge(record)" color="gold">
            {{ tv('roundBadge', { n: record.current_task?.round ?? 0 }) }}
          </a-tag>
        </a-space>
      </template>

      <template v-else-if="column.key === 'assignee'">
        <a-tooltip v-if="assigneeHoverLines(record).length > 1">
          <template #title>
            <div v-for="line in assigneeHoverLines(record)" :key="line">{{ line }}</div>
          </template>
          <span>{{ assigneeCellLabel(record) }}</span>
        </a-tooltip>
        <span v-else-if="assigneeCellLabel(record)">{{ assigneeCellLabel(record) }}</span>
        <span v-else class="text-muted">{{ tv('unassigned') }}</span>
      </template>

      <template v-else-if="column.key === 'action'">
        <a-button type="link" size="small" @click.stop="goProcess(record)">
          {{ tv('goProcess') }}
        </a-button>
      </template>
    </template>
  </a-table>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { TableColumnsType } from 'ant-design-vue'

import type { WorkItem } from '@/modules/project/api/work-item'
import { useProjectI18n } from '@/modules/project/i18n/use-project-i18n'
import {
  displayPipelineStage,
  getWorkItemWorkspacePath,
  pipelineStageLabelKey,
  workItemStatusColor,
} from '@/modules/project/utils/work-item-navigation'
import {
  formatLanguagePairLabel,
  languagePairSummaryFromWorkItem,
} from '@/modules/project/utils/language-pair-summary'
import { formatTaskUserLabel } from '@/modules/task/utils/task-display'
import WorkItemTaskTimeline from './WorkItemTaskTimeline.vue'

defineProps<{
  items: WorkItem[]
  loading?: boolean
}>()

const router = useRouter()
const { t } = useI18n()
const { tv } = useProjectI18n('detail')

const expandable = {
  columnWidth: 48,
  expandedRowRender: (record: WorkItem) => h(WorkItemTaskTimeline, { workItem: record }),
}

const columns = computed<TableColumnsType>(() => [
  {
    title: tv('colFileName'),
    key: 'fileName',
    dataIndex: 'file_name',
    ellipsis: true,
    width: 220,
  },
  {
    title: tv('colLanguage'),
    key: 'language',
    width: 160,
  },
  {
    title: tv('colStatus'),
    key: 'status',
    width: 200,
  },
  {
    title: tv('colAssignee'),
    key: 'assignee',
    width: 140,
  },
  {
    title: tv('colAction'),
    key: 'action',
    width: 120,
    fixed: 'right',
  },
])

function displayStage(item: WorkItem): string {
  return displayPipelineStage(item)
}

function statusLabel(status: string): string {
  const stageKey = pipelineStageLabelKey(status)
  if (stageKey !== 'statusUnknown') {
    return tv(stageKey)
  }
  const keyMap: Record<string, string> = {
    pending: 'statusPending',
    processing: 'statusProcessing',
    submitted: 'statusSubmitted',
    completed: 'statusCompleted',
    rejected_at_assignment: 'statusRejected',
    no_task: 'statusNoTask',
  }
  return tv(keyMap[status] || 'statusUnknown')
}

function assigneeHoverLines(item: WorkItem): string[] {
  const summary = item.active_review_summary
  if (summary && summary.count >= 1 && summary.assignees?.length) {
    return summary.assignees.map((row) => {
      const tasks =
        row.active_task_count > 1 ? ` (${tv('assigneeTaskCount', { n: row.active_task_count })})` : ''
      return `${row.name || row.user_id}${tasks}`
    })
  }
  if (item.current_task?.assignee_id || item.current_task?.assignee) {
    return [
      formatTaskUserLabel(item.current_task?.assignee, item.current_task?.assignee_id),
    ]
  }
  return []
}

function assigneeCellLabel(item: WorkItem): string {
  const summary = item.active_review_summary
  if (summary && summary.count >= 2) {
    return tv('multiReviewers', { n: summary.count })
  }
  if (summary && summary.count === 1 && summary.assignees?.[0]) {
    return summary.assignees[0].name || String(summary.assignees[0].user_id)
  }
  if (item.current_task?.assignee_id || item.current_task?.assignee) {
    return formatTaskUserLabel(item.current_task?.assignee, item.current_task?.assignee_id)
  }
  return ''
}

function languageLabel(code?: string | null): string {
  if (!code) return t('common.none')
  const key = `auth.language.${languageKey(code)}`
  const translated = t(key)
  return translated === key ? code : translated
}

function languagePairLabel(item: WorkItem): string {
  return formatLanguagePairLabel(
    languagePairSummaryFromWorkItem(item),
    (code) => {
      if (!code) return t('common.none')
      return languageLabel(code)
    },
    t('common.none'),
  )
}

function languageKey(code: string): string {
  const map: Record<string, string> = {
    zh: 'chinese',
    en: 'english',
    ja: 'japanese',
    ko: 'korean',
    fr: 'french',
    de: 'german',
    es: 'spanish',
    ru: 'russian',
    ar: 'arabic',
    pt: 'portuguese',
  }
  return map[code] || code
}

function showRoundBadge(item: WorkItem): boolean {
  const round = item.current_task?.round ?? 0
  return round > 1 || (round >= 1 && item.status === 'submitted')
}

function goProcess(item: WorkItem) {
  router.push(getWorkItemWorkspacePath(item))
}
</script>

<style lang="less" scoped>
.text-muted {
  color: #8c8c8c;
}
</style>
